import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './PatentApi';
import { Patent, PatentRequest, PatentSearchRequest, ReclaimRequest } from './PatentTypes';

const patentKey = getQueryKey('patent');

export function getPatentPagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Patent>> {
  return {
    queryKey: patentKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getPatentOptions(patentId: Id): UseSuspenseQueryOptions<Patent> {
  return {
    queryKey: patentKey.one(patentId),
    queryFn: () => api.get(patentId),
  };
}

export function usePatentEdit(patentId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: PatentRequest) => api.edit(patentId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: patentKey.root(),
      }),
  });
}

export function usePatentCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: PatentRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: patentKey.root(),
      }),
  });
}

export function usePatentDelete(patentId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(patentId),
    onSuccess: () => invalidateAfterDelete(queryClient, patentKey, patentId),
  });
}

export function useSearchPatentByIdentifiers() {
  return useMutation({
    mutationFn: (request: PatentSearchRequest) => api.searchByIdentifiers(request),
  });
}

export function useReclaimAuthorship() {
  return useMutation({
    mutationFn: ({ patentId, authorId }: ReclaimRequest) => api.reclaimAuthorship(patentId, authorId),
  });
}
